import { useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { env } from "../env.js";
import { useMe, useSetupStatus } from "../api/hooks.js";
import { ForbiddenScreen, LoadingScreen } from "./screens.js";

function redirectToLogin(): void {
  const next = encodeURIComponent(window.location.href);
  window.location.assign(`${env.apiBase}/auth/login?next=${next}`);
}

// Wraps the admin shell. Order: setup-status check first (uninitialized ->
// SetupWizard), then session (401 -> login redirect, non-admin -> Forbidden).
export function AdminGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const setup = useSetupStatus();
  const me = useMe({ enabled: setup.data?.initialized === true });

  const uninitialized = setup.data && !setup.data.initialized;

  // While uninitialized, force the /setup route.
  useEffect(() => {
    if (uninitialized && location.pathname !== "/setup") {
      navigate("/setup", { replace: true });
    }
  }, [uninitialized, location.pathname, navigate]);

  if (setup.isLoading) return <LoadingScreen label="Checking setup…" />;

  // Setup not yet completed: render the setup route's element (App routes
  // /setup to the wizard). Returning children lets the router show it.
  if (uninitialized) {
    if (location.pathname === "/setup") return <>{children}</>;
    return <LoadingScreen label="Redirecting to setup…" />;
  }

  if (me.isLoading) return <LoadingScreen label="Authenticating…" />;

  if (me.data === null) {
    redirectToLogin();
    return <LoadingScreen label="Redirecting to sign in…" />;
  }

  if (me.error) {
    if (me.error.status === 401) {
      redirectToLogin();
      return <LoadingScreen label="Redirecting to sign in…" />;
    }
    return <ForbiddenScreen />;
  }

  if (me.data && me.data.role !== "ADMIN") {
    return <ForbiddenScreen />;
  }

  return <>{children}</>;
}
